// Bank account -> eloNest account picker, shared by Bank Feeds and the
// Accounts "Connect a bank" setup. Values: '' (not chosen), 'create' (new
// eloNest account in the connection's business), 'create:<business id>' or
// 'create:personal' (new account in that business), an account id, or 'ignore'.
// With groups (from GET /plaid/items/:id/suggestions) the list covers every
// business the user may manage Bank Feeds for, grouped by business.
import React from 'react';

export const createValue = (businessId) => (businessId === null || businessId === undefined ? 'create:personal' : `create:${businessId}`);

export function mappingPayload(value) {
  if (value === 'ignore') return { ignored: true };
  if (value === 'create') return { create: true };
  if (typeof value === 'string' && value.startsWith('create:')) {
    const raw = value.slice(7);
    return { create: true, business_id: raw === 'personal' ? null : Number(raw) };
  }
  return { account_id: value ? Number(value) : null };
}

export const sameScope = (businessId) => (a) => (businessId ? Number(a.business_id) === Number(businessId) : !a.business_id);

// Pre-selection for an unmapped bank account: the single clear match, else a
// new account in the selected business, else in the connection's business.
// Never falls through to whichever business happens to be listed first.
export function defaultChoice(suggestion, itemBusinessId, groups, selectedBusinessId = null) {
  if (suggestion?.suggested?.account_id) return String(suggestion.suggested.account_id);
  if (!groups) return 'create';
  const has = (id) => groups.some((g) => (g.business_id ?? null) === (id ?? null));
  if (selectedBusinessId && has(Number(selectedBusinessId))) return createValue(Number(selectedBusinessId));
  if (has(itemBusinessId ?? null)) return createValue(itemBusinessId);
  return '';
}

// Plain words for a pick, for the review step before it is saved.
export function choiceText(value, groups, accounts) {
  const v = String(value ?? '');
  if (v === 'ignore') return 'not be imported';
  if (v === '') return 'have no eloNest account';
  if (v === 'create') return 'get a new eloNest account';
  if (v.startsWith('create:')) {
    const raw = v.slice(7);
    const g = (groups || []).find((x) => String(x.business_id ?? 'personal') === raw);
    return `get a new eloNest account in ${g ? g.business_name : (raw === 'personal' ? 'Personal' : 'that business')}`;
  }
  for (const g of groups || []) {
    const a = g.accounts.find((x) => String(x.id) === v);
    if (a) return `go to ${a.account_name} (${g.business_name})`;
  }
  const a = (accounts || []).find((x) => String(x.id) === v);
  return a ? `go to ${a.account_name}` : 'go to the chosen account';
}

// "Business name" of the group holding an account id (for tags), or null.
export function businessOf(groups, accountId) {
  for (const g of groups || []) if (g.accounts.some((a) => String(a.id) === String(accountId))) return g.business_name;
  return null;
}

export default function MappingSelect({ pa, accounts, groups, suggestion, businessId, value, onChange, disabled, allowCreate = true, allowEmpty = true, mapped = false }) {
  // A saved mapping (mapped) shows no "(suggested)" tag.
  const suggestedId = mapped ? null : suggestion?.suggested?.account_id;
  const free = (a) => !a.linked_to || Number(a.linked_to) === Number(pa.id) || String(a.id) === String(value);
  // The current mapping is always selectable, even when the option list does
  // not include it (list still loading, or scoped to another business), so a
  // mapped row never shows "Choose…" or snaps to another choice.
  const mappedId = /^\d+$/.test(String(value || '')) ? String(value) : null;
  const known = mappedId && (groups
    ? groups.some((g) => g.accounts.some((a) => String(a.id) === mappedId))
    : (accounts || []).filter(sameScope(businessId)).some((a) => String(a.id) === mappedId));
  const orphan = mappedId && !known;
  return (
    <select aria-label={`eloNest account for ${pa.name}`} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
      {allowEmpty && <option value="">Choose…</option>}
      {groups ? (
        <>
          {orphan && <option value={value}>{pa.account_name || 'Current account'}</option>}
          {/* Existing accounts, grouped by business; new-account choices are kept in their own group below. */}
          {groups.filter((g) => g.accounts.some(free)).map((g) => (
            <optgroup key={g.business_id ?? 'personal'} label={g.business_name}>
              {g.accounts.filter(free).map((a) => (
                <option key={a.id} value={a.id}>{a.account_name}{Number(a.id) === Number(suggestedId) ? ' (suggested)' : ''}</option>
              ))}
            </optgroup>
          ))}
          {allowCreate && groups.length > 0 && (
            <optgroup label="Create a new account">
              {groups.map((g) => (
                <option key={`create-${g.business_id ?? 'personal'}`} value={createValue(g.business_id)}>{`Create new in ${g.business_name}`}</option>
              ))}
            </optgroup>
          )}
        </>
      ) : (
        <>
          {orphan && <option value={value}>{pa.account_name || 'Current account'}</option>}
          {(accounts || []).filter(sameScope(businessId)).map((a) => (
            <option key={a.id} value={a.id}>{a.account_name}</option>
          ))}
          {allowCreate && <optgroup label="Create a new account"><option value="create">Create new eloNest account</option></optgroup>}
        </>
      )}
      <option value="ignore">Don't import this account</option>
    </select>
  );
}